import { Platform } from 'react-native';
import type { AppDispatch } from '../redux/Store';
import {
  setPrinterProfileStatus,
  type PrinterProfile,
  type PrinterProfileStatus,
  type PrinterProfileStatusSource,
} from '../redux/SettingsSlice';
import {
  listQzPrinters,
  discoverBleDevices,
  scanUsbDevices,
  scanSerialPorts,
} from './thermalReceiptService';
import { isElectronRuntime } from '../utils/isElectron';
import { hasBluetoothPermissions } from '../utils/bluetoothPermissions';

function dispatchPrinterProfileStatus(
  dispatch: AppDispatch,
  profileId: string,
  status: Omit<PrinterProfileStatus, 'updatedAt'> & { updatedAt?: number },
): void {
  dispatch(
    setPrinterProfileStatus({
      id: profileId,
      status,
    }),
  );
}

function parseUsbId16(raw: string | undefined): number | undefined {
  const value = String(raw || '').trim();
  if (!value) return undefined;
  const parsed = /^0x/i.test(value)
    ? parseInt(value.replace(/^0x/i, ''), 16)
    : parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 0xffff) {
    return undefined;
  }
  return parsed;
}

export function markPrinterProfileChecking(
  dispatch: AppDispatch,
  profileId: string,
): void {
  dispatchPrinterProfileStatus(dispatch, profileId, {
    kind: 'checking',
    message: 'Checking availability...',
    source: 'discovery',
  });
}

export function markPrinterProfilePrintSuccess(
  dispatch: AppDispatch,
  profileId: string,
  source: PrinterProfileStatusSource,
): void {
  const now = Date.now();
  dispatchPrinterProfileStatus(dispatch, profileId, {
    kind: 'available',
    message:
      source === 'test' ? 'Test print succeeded' : 'Last print succeeded',
    source,
    lastSuccessAt: now,
  });
}

export function markPrinterProfilePrintFailure(
  dispatch: AppDispatch,
  profileId: string,
  source: PrinterProfileStatusSource,
  message: string,
): void {
  const now = Date.now();
  dispatchPrinterProfileStatus(dispatch, profileId, {
    kind: 'failed',
    message: message || 'Last print failed',
    source,
    lastFailureAt: now,
  });
}

export async function checkPrinterProfileAvailability(
  dispatch: AppDispatch,
  profile: PrinterProfile,
): Promise<void> {
  // Electron-specific discovery via IPC to main process
  if (isElectronRuntime()) {
    return checkElectronProfileAvailability(dispatch, profile);
  }

  // These modes only get a reliable status from an actual print/test attempt.
  // Do not overwrite that result with a discovery-only "checking" state.
  if (profile.mode === 'tcp' || profile.mode === 'web-bluetooth') {
    return;
  }

  markPrinterProfileChecking(dispatch, profile.id);

  try {
    if (profile.mode === 'bluetooth') {
      if (Platform.OS !== 'android') {
        return;
      }

      const targetMac = String(profile.bluetoothAddress || '').trim();
      if (!targetMac) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'not_detected',
          message: 'Missing Bluetooth address',
          source: 'discovery',
        });
        return;
      }

      if (!(await hasBluetoothPermissions())) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'unknown',
          message: 'Bluetooth permission required',
          source: 'discovery',
        });
        return;
      }

      const devices = await discoverBleDevices({ duration: 5 });
      const found = devices.some(
        device => (device.inner_mac_address || '').trim() === targetMac,
      );
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found ? 'Detected nearby' : 'Not detected nearby',
        source: 'discovery',
      });
      return;
    }

    if (profile.mode === 'usb') {
      if (Platform.OS !== 'android' && Platform.OS !== 'web') {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'unknown',
          message: 'USB status is unavailable on this device',
          source: 'discovery',
        });
        return;
      }

      const vendorId = parseUsbId16(profile.usbVendorId);
      const productId = parseUsbId16(profile.usbProductId);
      if (vendorId === undefined || productId === undefined) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'not_detected',
          message: 'Missing USB vendor/product ID',
          source: 'discovery',
        });
        return;
      }

      const devices = await scanUsbDevices(false);
      const found = devices.some(
        device =>
          Number((device as any)?.vendor_id) === vendorId &&
          Number((device as any)?.product_id) === productId,
      );
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found ? 'USB device detected' : 'USB device not detected',
        source: 'discovery',
      });
      return;
    }

    if (profile.mode === 'network') {
      if (Platform.OS !== 'web') {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'unknown',
          message: 'Status updates after a print attempt',
          source: 'discovery',
        });
        return;
      }

      const printerName = String(profile.printerName || '').trim();
      if (!printerName) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'not_detected',
          message: 'Missing QZ printer name',
          source: 'discovery',
        });
        return;
      }

      const printers = await listQzPrinters();
      const found = printers.some(
        name => String(name || '').trim() === printerName,
      );
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found ? 'QZ printer available' : 'QZ printer not found',
        source: 'discovery',
      });
      return;
    }

    if (profile.mode === 'web-serial') {
      if (Platform.OS !== 'web') {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'unknown',
          message: 'Serial status is unavailable on this device',
          source: 'discovery',
        });
        return;
      }

      const ports = await scanSerialPorts(false);
      const vendorId = parseUsbId16(profile.serialUsbVendorId);
      const productId = parseUsbId16(profile.serialUsbProductId);
      const hasFilter = vendorId !== undefined || productId !== undefined;
      const found = !hasFilter
        ? ports.length > 0
        : ports.some(
            port =>
              (vendorId === undefined ||
                Number((port as any)?.usb_vendor_id) === vendorId) &&
              (productId === undefined ||
                Number((port as any)?.usb_product_id) === productId),
          );
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found
          ? 'Serial port authorized'
          : 'Serial port not authorized',
        source: 'discovery',
      });
      return;
    }

    return;
  } catch {
    dispatchPrinterProfileStatus(dispatch, profile.id, {
      kind: 'unknown',
      message: 'Could not check printer status',
      source: 'discovery',
    });
  }
}

async function checkElectronProfileAvailability(
  dispatch: AppDispatch,
  profile: PrinterProfile,
): Promise<void> {
  // TCP and web-bluetooth only get status from actual print attempts
  if (profile.mode === 'tcp' || profile.mode === 'web-bluetooth') {
    return;
  }

  markPrinterProfileChecking(dispatch, profile.id);

  try {
    if (profile.mode === 'spooler') {
      const printerName = String(profile.printerName || '').trim();
      if (!printerName) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'not_detected',
          message: 'Missing printer name',
          source: 'discovery',
        });
        return;
      }
      const { listPrinters } = await import('./electronPrintService');
      const printers = await listPrinters();
      const found = printers.some(
        p => String(p.name || '').trim() === printerName,
      );
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found ? 'Printer installed' : 'Printer not found in OS',
        source: 'discovery',
      });
      return;
    }

    if (profile.mode === 'serial') {
      const serialPath = String(profile.serialPath || '').trim();
      if (!serialPath) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'not_detected',
          message: 'Missing serial port path',
          source: 'discovery',
        });
        return;
      }
      const { listSerialPorts } = await import('./electronPrintService');
      const ports = await listSerialPorts();
      const found = ports.some(p => String(p.path || '').trim() === serialPath);
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found ? 'Serial port detected' : 'Serial port not found',
        source: 'discovery',
      });
      return;
    }

    if (profile.mode === 'usb') {
      const vendorId = parseUsbId16(profile.usbVendorId);
      const productId = parseUsbId16(profile.usbProductId);
      if (vendorId === undefined || productId === undefined) {
        dispatchPrinterProfileStatus(dispatch, profile.id, {
          kind: 'not_detected',
          message: 'Missing USB vendor/product ID',
          source: 'discovery',
        });
        return;
      }
      const { listUsbDevices } = await import('./electronPrintService');
      const devices = await listUsbDevices();
      const found = devices.some(
        d => d.vendorId === vendorId && d.productId === productId,
      );
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: found ? 'available' : 'not_detected',
        message: found ? 'USB device detected' : 'USB device not found',
        source: 'discovery',
      });
      return;
    }

    // For bluetooth (BLE via Web Bluetooth in Electron), no background scan
    if (profile.mode === 'bluetooth') {
      dispatchPrinterProfileStatus(dispatch, profile.id, {
        kind: 'unknown',
        message: 'Status updates after a print attempt',
        source: 'discovery',
      });
      return;
    }

    // network (QZ), web-serial — fall through to status after print
    dispatchPrinterProfileStatus(dispatch, profile.id, {
      kind: 'unknown',
      message: 'Status updates after a print attempt',
      source: 'discovery',
    });
  } catch {
    dispatchPrinterProfileStatus(dispatch, profile.id, {
      kind: 'unknown',
      message: 'Could not check printer status',
      source: 'discovery',
    });
  }
}
